import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Upload, Film, BarChart3, Settings, Clock, Eye, Star, 
  CheckCircle, XCircle, AlertCircle, Plus, Edit, Trash2,
  TrendingUp, Users, Play, Filter, DollarSign, Wallet, CreditCard, Heart, ArrowDownRight, RefreshCw, Lock, Unlock, Crown, Bell,
  ShoppingBag, Package, Store, Send, Building2, Banknote, Image, X, Loader2, Search, ArrowUpDown, Tv, Calendar, ChevronDown, ChevronRight, Globe, AlertTriangle, BookOpen
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { useAuth } from '../contexts/AuthContext';
import { companyApi, analyticsApi, creatorEarningsApi, donationsApi, premiumContentApi, shopApi, unifiedEarningsApi, creatorPayoutApi, seriesApi, formatDuration, editRequestsApi, notificationsApi, contentApi, legalApi, creatorChartsApi } from '../lib/api';
import FinancialsDashboard from '../components/FinancialsDashboard';
import CreatorAudienceTab from '../components/CreatorAudienceTab';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const COLORS = ['#8B5CF6', '#00FF9D', '#3B82F6', '#F59E0B', '#EF4444'];

/* ── Content Row (for Films) ── */
const ContentRow = ({ film, getStatusBadge, getOptimizationBadge, onDetails, onResubmit }) => (
  <div className="admin-card p-4 flex gap-5 hover:border-[#8B5CF6]/40 transition-all group" data-testid={`content-${film.film_id}`}>
    <div className="relative w-44 flex-shrink-0">
      <img src={film.thumbnail} alt={film.title} className="w-full h-24 object-cover rounded-lg" />
      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
        <Play className="w-8 h-8 text-white" />
      </div>
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-start justify-between gap-4">
        <h3 className="font-semibold line-clamp-1">{film.title}</h3>
        <div className="flex items-center gap-2">
          {film.is_scheduled && (
            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[#8B5CF6]/20 text-[#8B5CF6]" data-testid={`scheduled-badge-${film.film_id}`}>SCHEDULED</span>
          )}
          {getStatusBadge(film.status)}
          {getOptimizationBadge && getOptimizationBadge(film)}
        </div>
      </div>
      <p className="text-sm text-gray-400 line-clamp-1 mt-0.5">{film.description}</p>
      {film.status === 'pending_optimization' && film.optimization_status === 'failed' && (
        <p className="text-xs text-red-400 mt-1">Please re-upload the video in MP4 format.</p>
      )}
      <div className="flex items-center gap-5 mt-3 text-sm">
        <span className="flex items-center gap-1 text-gray-300"><Eye className="w-3.5 h-3.5 text-[#8B5CF6]" />{film.views?.toLocaleString() || 0}</span>
        <span className="flex items-center gap-1 text-gray-300"><Star className="w-3.5 h-3.5 text-yellow-400" />{film.avg_rating?.toFixed(1) || '0.0'}</span>
        {film.genres?.slice(0, 2).map(g => <span key={g} className="badge text-xs">{g}</span>)}
        {film.release_date && (
          <span className="flex items-center gap-1 text-gray-500 text-xs">
            <Calendar className="w-3 h-3" /> {new Date(film.release_date + 'T00:00:00').toLocaleDateString('en-US')}
          </span>
        )}
      </div>
      {film.status === 'rejected' && film.rejection_feedback && (
        <div className="mt-2 p-2 bg-red-500/10 border border-red-500/20 rounded text-xs text-red-400">
          <strong>Feedback:</strong> {film.rejection_feedback}
        </div>
      )}
      {film.paused_reason && film.status === 'paused' && (
        <div className="mt-2 p-2 bg-orange-500/10 border border-orange-500/20 rounded text-xs text-orange-400">
          <strong>Paused reason:</strong> {film.paused_reason}
        </div>
      )}
      {film.removed_reason && film.status === 'removed' && (
        <div className="mt-2 p-2 bg-red-500/10 border border-red-500/20 rounded text-xs text-red-400">
          <strong>Removed reason:</strong> {film.removed_reason}
        </div>
      )}
    </div>
    <div className="flex flex-col gap-2 self-center">
      {film.status === 'approved' && (
        <>
        <Button size="sm" variant="outline" className="border-[#8B5CF6]/30 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={() => onDetails(film)} data-testid={`view-performance-${film.film_id}`}>
          <BarChart3 className="w-4 h-4 mr-1" /> <span className="text-xs">Performance</span>
        </Button>
        <Button size="sm" variant="outline" className="border-[#8B5CF6]/30 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={() => onResubmit?.(film.film_id)} data-testid={`edit-approved-${film.film_id}`}>
          <Edit className="w-3.5 h-3.5 mr-1" /> <span className="text-xs">Edit</span>
        </Button>
        </>
      )}
      {(film.status === 'paused' || film.status === 'rejected') && (
        <Button size="sm" className="bg-[#8B5CF6]/20 text-[#A78BFA] hover:bg-[#8B5CF6]/30"
          onClick={() => onResubmit?.(film.film_id)} data-testid={`resubmit-${film.film_id}`}>
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> <span className="text-xs">Resubmit</span>
        </Button>
      )}
    </div>
  </div>
);

/* ── Series Row with expandable episode list ── */
const SeriesRow = ({ series, getStatusBadge, onDetails, onResubmit }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [episodes, setEpisodes] = React.useState(null);
  const [loadingEps, setLoadingEps] = React.useState(false);
  const [showAddSeason, setShowAddSeason] = React.useState(false);
  const [addEpisodeSeason, setAddEpisodeSeason] = React.useState(null);

  const toggleExpand = async () => {
    if (!expanded && episodes === null) {
      setLoadingEps(true);
      try {
        const data = await seriesApi.getAllEpisodes(series.film_id);
        setEpisodes(data.episodes || data || []);
      } catch { setEpisodes([]); }
      finally { setLoadingEps(false); }
    }
    setExpanded(!expanded);
  };

  const refreshEpisodes = async () => {
    try {
      const data = await seriesApi.getAllEpisodes(series.film_id);
      setEpisodes(data.episodes || data || []);
    } catch {}
  };

  // Group episodes by season
  const groupedBySeason = React.useMemo(() => {
    if (!episodes) return {};
    const m = {};
    episodes.forEach(ep => {
      const s = ep.season_number || 1;
      if (!m[s]) m[s] = [];
      m[s].push(ep);
    });
    Object.keys(m).forEach(s => m[s].sort((a, b) => a.episode_number - b.episode_number));
    return m;
  }, [episodes]);

  return (
    <div className="admin-card overflow-hidden" data-testid={`content-${series.film_id}`}>
      <div className="p-4 flex gap-5 hover:bg-[#8B5CF6]/5 cursor-pointer transition-all group" onClick={toggleExpand}>
        <div className="relative w-44 flex-shrink-0">
          <img src={series.thumbnail} alt={series.title} className="w-full h-24 object-cover rounded-lg" />
          <div className="absolute top-2 left-2">
            <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-[#00FF9D]/80 text-black">SERIES</span>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4">
            <h3 className="font-semibold line-clamp-1">{series.title}</h3>
            <div className="flex items-center gap-2">
              {series.is_scheduled && (
                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[#8B5CF6]/20 text-[#8B5CF6]" data-testid={`scheduled-badge-${series.film_id}`}>SCHEDULED</span>
              )}
              {getStatusBadge(series.status)}
            </div>
          </div>
          <p className="text-sm text-gray-400 line-clamp-1 mt-0.5">{series.description}</p>
          <div className="flex items-center gap-5 mt-3 text-sm">
            <span className="flex items-center gap-1 text-gray-300"><Eye className="w-3.5 h-3.5 text-[#8B5CF6]" />{series.views?.toLocaleString() || 0}</span>
            <span className="flex items-center gap-1 text-gray-300"><Star className="w-3.5 h-3.5 text-yellow-400" />{series.avg_rating?.toFixed(1) || '0.0'}</span>
            {series.genres?.slice(0, 2).map(g => <span key={g} className="badge text-xs">{g}</span>)}
            {series.release_date && (
              <span className="flex items-center gap-1 text-gray-500 text-xs">
                <Calendar className="w-3 h-3" /> {new Date(series.release_date + 'T00:00:00').toLocaleDateString('en-US')}
              </span>
            )}
          </div>
          {series.status === 'rejected' && series.rejection_feedback && (
            <div className="mt-2 p-2 bg-red-500/10 border border-red-500/20 rounded text-xs text-red-400">
              <strong>Feedback:</strong> {series.rejection_feedback}
            </div>
          )}
          {series.paused_reason && series.status === 'paused' && (
            <div className="mt-2 p-2 bg-orange-500/10 border border-orange-500/20 rounded text-xs text-orange-400">
              <strong>Paused reason:</strong> {series.paused_reason}
            </div>
          )}
          {series.removed_reason && series.status === 'removed' && (
            <div className="mt-2 p-2 bg-red-500/10 border border-red-500/20 rounded text-xs text-red-400">
              <strong>Removed reason:</strong> {series.removed_reason}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2 self-center">
          {series.status === 'approved' && (
            <>
              <Button size="sm" variant="outline" className="border-[#8B5CF6]/30 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => { e.stopPropagation(); onDetails(series); }} data-testid={`view-performance-${series.film_id}`}>
                <BarChart3 className="w-4 h-4 mr-1" /> <span className="text-xs">Performance</span>
              </Button>
              <Button size="sm" variant="outline" className="border-[#8B5CF6]/30 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => { e.stopPropagation(); onResubmit?.(series.film_id); }} data-testid={`edit-approved-${series.film_id}`}>
                <Edit className="w-3.5 h-3.5 mr-1" /> <span className="text-xs">Edit</span>
              </Button>
            </>
          )}
          {(series.status === 'paused' || series.status === 'rejected') && (
            <Button size="sm" className="bg-[#8B5CF6]/20 text-[#A78BFA] hover:bg-[#8B5CF6]/30"
              onClick={(e) => { e.stopPropagation(); onResubmit?.(series.film_id); }} data-testid={`resubmit-${series.film_id}`}>
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> <span className="text-xs">Resubmit</span>
            </Button>
          )}
          <div className={`w-6 h-6 flex items-center justify-center text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
          </div>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-white/5 bg-[#030014]/50 p-4">
          {loadingEps ? (
            <p className="text-sm text-gray-500 text-center py-4">Loading episodes...</p>
          ) : episodes && episodes.length > 0 ? (
            <div className="space-y-4">
              {Object.entries(groupedBySeason).map(([season, eps]) => (
                <div key={season}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-[#00FF9D]">Season {season}</p>
                    <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setAddEpisodeSeason({ number: parseInt(season), count: eps.length }); }}
                      className="h-6 px-2 text-[10px] text-[#00FF9D] hover:bg-[#00FF9D]/10" data-testid={`add-episode-btn-s${season}-${series.film_id}`}>
                      <Plus className="w-3 h-3 mr-0.5" /> Add Episode
                    </Button>
                  </div>
                  <div className="space-y-1.5">
                    {eps.map(ep => (
                      <div key={ep.episode_id} className={`flex items-center gap-3 p-2.5 rounded-lg border text-sm ${ep.status === 'rejected' ? 'bg-red-500/5 border-red-500/20' : 'bg-[#0F0B1E] border-white/5'}`}>
                        {ep.thumbnail && <img src={ep.thumbnail} alt="" className="w-16 h-10 object-cover rounded flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">E{ep.episode_number}: {ep.title}</span>
                            {ep.status && ep.status !== 'approved' && (
                              <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${
                                ep.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                                ep.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                                'bg-gray-500/20 text-gray-400'
                              }`}>{ep.status?.toUpperCase()}</span>
                            )}
                          </div>
                          {ep.description && <p className="text-xs text-gray-500 truncate">{ep.description}</p>}
                          {ep.status === 'rejected' && ep.rejection_feedback && (
                            <p className="text-xs text-red-400 mt-1 truncate">Feedback: {ep.rejection_feedback}</p>
                          )}
                        </div>
                        <span className="text-xs text-gray-500 flex-shrink-0">
                          {ep.video_file ? 'Uploaded' : 'No video'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-4">No episodes found</p>
          )}
          {(series.status === 'approved' || series.status === 'rejected' || series.status === 'pending' || series.status === 'paused') && (
            <div className="mt-4 pt-3 border-t border-white/5 flex gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setShowAddSeason(true); }}
                className="border-[#00FF9D]/30 text-[#00FF9D] hover:bg-[#00FF9D]/10" data-testid={`add-season-btn-${series.film_id}`}>
                <Plus className="w-4 h-4 mr-1" /> Add Season
              </Button>
              {series.status === 'approved' && (
                <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onDetails(series); }}
                  className="border-[#8B5CF6]/30 text-[#8B5CF6] hover:bg-[#8B5CF6]/10" data-testid={`edit-episodes-btn-${series.film_id}`}>
                  <Edit className="w-4 h-4 mr-1" /> Edit Episodes
                </Button>
              )}
              {(series.status === 'paused' || series.status === 'rejected') && (
                <Button size="sm" onClick={(e) => { e.stopPropagation(); onResubmit?.(series.film_id); }}
                  className="bg-[#8B5CF6]/20 text-[#A78BFA] hover:bg-[#8B5CF6]/30" data-testid={`resubmit-episodes-btn-${series.film_id}`}>
                  <RefreshCw className="w-4 h-4 mr-1" /> Edit & Resubmit
                </Button>
              )}
            </div>
          )}
          <AddSeasonDialog
            open={showAddSeason}
            onOpenChange={setShowAddSeason}
            seriesId={series.film_id}
            seriesTitle={series.title}
            existingSeasons={episodes ? [...new Set(episodes.map(ep => ep.season_number))] : []}
            onSeasonAdded={refreshEpisodes}
          />
          <AddEpisodeDialog
            open={!!addEpisodeSeason}
            onOpenChange={(open) => { if (!open) setAddEpisodeSeason(null); }}
            seriesId={series.film_id}
            seriesTitle={series.title}
            seasonNumber={addEpisodeSeason?.number || 1}
            existingEpisodeCount={addEpisodeSeason?.count || 0}
            onEpisodeAdded={refreshEpisodes}
          />
        </div>
      )}
    </div>
  );
};


// ── Creator Orders Panel (imported) ──
import CreatorOrdersPanelComponent from '../components/CreatorOrdersPanel';
import CreatorProductsPanel from '../components/CreatorProductsPanel';
import AddSeasonDialog from '../components/AddSeasonDialog';
import AddEpisodeDialog from '../components/AddEpisodeDialog';

const CompanyDashboardPage = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [content, setContent] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [contentFilter, setContentFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [genreFilter, setGenreFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [earnings, setEarnings] = useState(null);
  const [donations, setDonations] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ iban: '', paypal_email: '' });
  const [selectedFilm, setSelectedFilm] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [premiumEligibility, setPremiumEligibility] = useState(null);
  const [premiumEarnings, setPremiumEarnings] = useState(null);
  const [settingPremium, setSettingPremium] = useState(null);
  const [premiumPrice, setPremiumPrice] = useState('');
  const [premiumSearch, setPremiumSearch] = useState('');
  const [premiumContentView, setPremiumContentView] = useState('movies');
  const [expandedSeries, setExpandedSeries] = useState(null);
  const [seriesEpisodes, setSeriesEpisodes] = useState({});
  const [selectedPremiumFilms, setSelectedPremiumFilms] = useState([]);
  const [selectedPremiumEpisodes, setSelectedPremiumEpisodes] = useState([]);
  const [showSetPremiumPopup, setShowSetPremiumPopup] = useState(false);
  const [batchPremiumPrice, setBatchPremiumPrice] = useState('');
  const [batchSubmitting, setBatchSubmitting] = useState(false);

  // Shop state
  const [shopProducts, setShopProducts] = useState([]);
  const [shopOrders, setShopOrders] = useState([]);

  // Unified Earnings state
  const [unifiedEarnings, setUnifiedEarnings] = useState(null);
  const [creatorWallet, setCreatorWallet] = useState(null);

  // Payout Configuration state
  const [payoutMethods, setPayoutMethods] = useState(null);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [showPayoutForm, setShowPayoutForm] = useState(false);
  const [showStripeManualSetup, setShowStripeManualSetup] = useState(false);
  const [payoutMethodForm, setPayoutMethodForm] = useState({
    method: 'stripe',
    stripe_account_id: '',
    payoneer_email: '',
    bank_name: '',
    bank_iban: '',
    bank_bic: '',
    bank_account_holder: ''
  });
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [selectedWithdrawMethod, setSelectedWithdrawMethod] = useState('');

  // Notification badge state (for tab)
  const [notifUnreadCount, setNotifUnreadCount] = useState(0);

  // Edit rejected content state
  const [editingContent, setEditingContent] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', description: '', thumbnail: '', video_file: '' });
  const [editEpisodes, setEditEpisodes] = useState([]);
  const [editEpisodesOriginal, setEditEpisodesOriginal] = useState([]);
  const [editUploading, setEditUploading] = useState(false);

  const [uploadForm, setUploadForm] = useState({
    content_type: 'film',
    title: '',
    description: '',
    youtube_id: '',
    vimeo_id: '',
    trailer_youtube_id: '',
    trailer_vimeo_id: '',
    duration: '',
    year: new Date().getFullYear(),
    genres: [],
    categories: [],
    episodes: [],
  });

  const [newEpisode, setNewEpisode] = useState({
    season_number: 1,
    episode_number: 1,
    title: '',
    description: '',
    youtube_id: '',
    vimeo_id: '',
    duration: 0,
  });

  // Agreement state
  const [showAgreement, setShowAgreement] = useState(false);
  const [agreementHtml, setAgreementHtml] = useState('');
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(null);

  // Drafts state
  const [drafts, setDrafts] = useState([]);
  const [deletingDraftId, setDeletingDraftId] = useState(null);
  const [confirmDeleteDraft, setConfirmDeleteDraft] = useState(null);

  const GENRE_OPTIONS = [
    'Action', 'Adventure', 'Animation', 'Biography', 'Comedy', 'Crime',
    'Documentary', 'Drama', 'Family', 'Fantasy', 'Historical', 'Horror',
    'Musical', 'Mystery', 'Romance', 'Sci-Fi', 'Sports', 'Thriller',
    'War', 'Western', 'Anime',
  ];

  const CATEGORY_OPTIONS = [
    'New Arrivals', 'Popular', 'Trending', 'Featured',
  ];

  // Fetch company data function - can be called to refresh
  const fetchCompanyData = useCallback(async (showToast = false) => {
    try {
      const companyData = await companyApi.getMyCompany();
      setCompany(companyData.company);

      if (companyData.company?.status === 'approved') {
        const [contentData, analyticsData] = await Promise.all([
          companyApi.getContent(),
          analyticsApi.getCompanyAnalytics(companyData.company.company_id),
        ]);
        setContent(contentData);
        setAnalytics(analyticsData);

        // Fetch earnings and donations
        try {
          const [earningsData, donationsData] = await Promise.all([
            creatorEarningsApi.getEarnings(),
            donationsApi.getReceived(),
          ]);
          setEarnings(earningsData);
          setDonations(donationsData);
          if (earningsData.payment_setup) {
            setPaymentForm({
              iban: earningsData.payment_setup.iban || '',
              paypal_email: earningsData.payment_setup.paypal_email || '',
            });
          }
        } catch (e) {
          console.error('Failed to fetch earnings:', e);
        }

        // Fetch premium content eligibility and earnings
        try {
          const [eligibilityData, premiumEarningsData] = await Promise.all([
            premiumContentApi.checkEligibility(),
            premiumContentApi.getCreatorEarnings(),
          ]);
          setPremiumEligibility(eligibilityData);
          setPremiumEarnings(premiumEarningsData);
        } catch (e) {
          console.error('Failed to fetch premium content data:', e);
        }

        // Fetch shop data
        try {
          const [productsData, ordersData] = await Promise.all([
            shopApi.getMyProducts(),
            shopApi.getMyOrders(),
          ]);
          setShopProducts(productsData.products || []);
          setShopOrders(ordersData.orders || []);
        } catch (e) {
          console.error('Failed to fetch shop data:', e);
        }

        // Fetch unified earnings and wallet
        try {
          const [unifiedData, walletData] = await Promise.all([
            unifiedEarningsApi.getEarnings(),
            unifiedEarningsApi.getWallet(),
          ]);
          setUnifiedEarnings(unifiedData);
          setCreatorWallet(walletData);
        } catch (e) {
          console.error('Failed to fetch unified earnings:', e);
        }

        // Fetch payout configuration
        try {
          const [payoutMethodsData, payoutHistoryData] = await Promise.all([
            creatorPayoutApi.getPayoutMethods(),
            creatorPayoutApi.getPayoutHistory(),
          ]);
          setPayoutMethods(payoutMethodsData);
          setPayoutHistory(payoutHistoryData.payouts || []);
        } catch (e) {
          console.error('Failed to fetch payout data:', e);
        }
        
        if (showToast) {
          toast.success('Studio approved! You can now upload content.');
        }
      } else if (showToast) {
        toast.info('Status checked - still pending review.');
      }
    } catch (error) {
      console.error('Failed to fetch company data:', error);
      if (showToast) {
        toast.error('Failed to check status');
      }
    } finally {
      setLoading(false);
      setCheckingStatus(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchCompanyData();
  }, [isAuthenticated, navigate, fetchCompanyData]);

  // Handle Stripe Connect return
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const stripeReturn = urlParams.get('stripe_return');
    const stripeRefresh = urlParams.get('stripe_refresh');
    
    if (stripeReturn === 'true') {
      // User returned from Stripe onboarding - check account status
      const checkStripeStatus = async () => {
        try {
          const status = await creatorPayoutApi.getStripeAccountStatus();
          if (status.connected) {
            toast.success('Stripe account connected successfully!');
          } else if (status.details_submitted) {
            toast.info('Stripe verification in progress. We\'ll update your status once verified.');
          } else {
            toast.warning('Stripe setup incomplete. Please complete all required information.');
          }
          fetchCompanyData();
        } catch (e) {
          console.error('Failed to check Stripe status:', e);
        }
      };
      checkStripeStatus();
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (stripeRefresh === 'true') {
      // User needs to restart onboarding
      toast.info('Please click "Continue Setup" to complete your Stripe verification.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [fetchCompanyData]);

  // Poll notification unread count for tab badge
  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const data = await notificationsApi.getAll(1);
        setNotifUnreadCount(data.unread_count || 0);
      } catch {}
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, []);

  // Check status button handler
  const handleCheckStatus = async () => {
    setCheckingStatus(true);
    await fetchCompanyData(true);
  };

  // Fetch drafts on mount
  useEffect(() => {
    const fetchDrafts = async () => {
      try {
        const res = await companyApi.getDrafts();
        setDrafts(res.drafts || []);
      } catch {}
    };
    fetchDrafts();
  }, []);

  const handleDeleteDraft = async (draftId) => {
    setDeletingDraftId(draftId);
    try {
      await companyApi.deleteDraft(draftId);
      setDrafts(prev => prev.filter(d => d.draft_id !== draftId));
      toast.success('Draft deleted');
    } catch (e) {
      toast.error(e.message || 'Failed to delete draft');
    } finally {
      setDeletingDraftId(null);
      setConfirmDeleteDraft(null);
    }
  };

  // PPV -> Free confirmation state
  const [confirmFreeDialog, setConfirmFreeDialog] = useState({ open: false, film: null });

  // Open edit dialog for rejected/paused content
  const openEditRejected = async (film) => {
    setEditForm({
      title: film.title || '',
      description: film.description || '',
      thumbnail: film.thumbnail || '',
      video_file: film.video_file || '',
    });
    setEditingContent(film);
    setEditEpisodes([]);
    setEditEpisodesOriginal([]);
    // Fetch episodes for series
    if (film.content_type === 'series') {
      try {
        const data = await seriesApi.getAllEpisodes(film.film_id);
        const eps = (data.episodes || data || []).map(ep => ({ ...ep }));
        setEditEpisodes(eps);
        setEditEpisodesOriginal(JSON.parse(JSON.stringify(eps)));
      } catch {}
    }
  };

  const handleEditThumbnailUpload = async (file) => {
    setEditUploading(true);
    try {
      const result = await companyApi.uploadThumbnail(file);
      setEditForm(prev => ({ ...prev, thumbnail: result.url }));
      toast.success('Thumbnail uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setEditUploading(false); }
  };

  const handleEditVideoUpload = async (file) => {
    setEditUploading(true);
    try {
      const result = await companyApi.uploadVideo(file, () => {});
      setEditForm(prev => ({ ...prev, video_file: result.url }));
      toast.success('Video uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setEditUploading(false); }
  };

  const handleEpThumbnailUpload = async (epIndex, file) => {
    setEditUploading(true);
    try {
      const result = await companyApi.uploadThumbnail(file);
      setEditEpisodes(prev => prev.map((ep, i) => i === epIndex ? { ...ep, thumbnail: result.url } : ep));
      toast.success('Episode thumbnail uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setEditUploading(false); }
  };

  const handleEpVideoUpload = async (epIndex, file) => {
    setEditUploading(true);
    try {
      const result = await companyApi.uploadVideo(file, () => {});
      setEditEpisodes(prev => prev.map((ep, i) => i === epIndex ? { ...ep, video_file: result.url } : ep));
      toast.success('Episode video uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setEditUploading(false); }
  };

  const handleEditResubmit = async () => {
    if (!editingContent) return;
    try {
      // Build only changed fields for the series/movie
      const updates = {};
      if (editForm.title !== editingContent.title) updates.title = editForm.title;
      if (editForm.description !== editingContent.description) updates.description = editForm.description;
      if (editForm.thumbnail !== editingContent.thumbnail) updates.thumbnail = editForm.thumbnail;
      if (editForm.video_file !== editingContent.video_file) updates.video_file = editForm.video_file;

      // Always send at least title to trigger status change
      if (!updates.title) updates.title = editForm.title;

      await contentApi.updateAndResubmit(editingContent.film_id, updates);

      // Update modified episodes
      if (editingContent.content_type === 'series' && editEpisodes.length > 0) {
        for (let i = 0; i < editEpisodes.length; i++) {
          const ep = editEpisodes[i];
          const orig = editEpisodesOriginal.find(o => o.episode_id === ep.episode_id);
          if (!orig) continue;

          const epUpdates = {};
          if (ep.title !== orig.title) epUpdates.title = ep.title;
          if (ep.description !== orig.description) epUpdates.description = ep.description;
          if (ep.thumbnail !== orig.thumbnail) epUpdates.thumbnail = ep.thumbnail;
          if (ep.video_file !== orig.video_file) epUpdates.video_file = ep.video_file;

          if (Object.keys(epUpdates).length > 0) {
            await editRequestsApi.updateAndResubmitEpisode(ep.episode_id, epUpdates);
          }
        }
      }

      toast.success('Content updated and resubmitted for review');
      setEditingContent(null);
      setEditEpisodes([]);
      setEditEpisodesOriginal([]);
      fetchCompanyData();
    } catch (e) { toast.error(e.message || 'Failed to resubmit'); }
  };

  // Premium content handlers
  const handleSetPremium = async (film) => {
    if (!premiumEligibility?.eligible) {
      toast.error('You are not eligible to sell premium content yet');
      return;
    }

    if (film.is_premium_content) {
      // Show confirmation before making free
      setConfirmFreeDialog({ open: true, film });
    } else {
      // Show price input
      setSettingPremium(film);
      setPremiumPrice('');
    }
  };

  const confirmMakeFree = async () => {
    const film = confirmFreeDialog.film;
    if (!film) return;
    try {
      await premiumContentApi.setContent(film.film_id, false, null);
      toast.success('Content is now free');
      setConfirmFreeDialog({ open: false, film: null });
      fetchCompanyData();
    } catch (e) {
      toast.error(e.message || 'Failed to update content');
    }
  };

  const loadSeriesEpisodes = async (seriesId) => {
    if (seriesEpisodes[seriesId]) return;
    try {
      const data = await seriesApi.getEpisodes(seriesId);
      setSeriesEpisodes(prev => ({ ...prev, [seriesId]: data.episodes || [] }));
    } catch (e) {
      toast.error('Failed to load episodes');
    }
  };

  const toggleExpandSeries = async (seriesId) => {
    if (expandedSeries === seriesId) {
      setExpandedSeries(null);
    } else {
      setExpandedSeries(seriesId);
      await loadSeriesEpisodes(seriesId);
    }
  };

  const toggleSelectFilm = (filmId) => {
    setSelectedPremiumFilms(prev =>
      prev.includes(filmId) ? prev.filter(id => id !== filmId) : [...prev, filmId]
    );
  };

  const toggleSelectEpisode = (epId) => {
    setSelectedPremiumEpisodes(prev =>
      prev.includes(epId) ? prev.filter(id => id !== epId) : [...prev, epId]
    );
  };

  const handleBatchSetPremium = async () => {
    const price = parseFloat(batchPremiumPrice);
    if (isNaN(price) || price < 0.50 || price > 9.99) {
      toast.error('Price must be between $0.50 and $9.99');
      return;
    }
    setBatchSubmitting(true);
    try {
      await premiumContentApi.setBatch(selectedPremiumFilms, selectedPremiumEpisodes, true, price);
      toast.success('Selected content set as premium!');
      setShowSetPremiumPopup(false);
      setBatchPremiumPrice('');
      setSelectedPremiumFilms([]);
      setSelectedPremiumEpisodes([]);
      setSeriesEpisodes({});
      fetchCompanyData();
    } catch (e) {
      toast.error(e.message || 'Failed to set premium');
    } finally {
      setBatchSubmitting(false);
    }
  };

  // Batch confirm free state
  const [confirmBatchFreeDialog, setConfirmBatchFreeDialog] = useState(false);

  const handleBatchRemovePremium = async () => {
    setConfirmBatchFreeDialog(true);
  };

  const confirmBatchMakeFree = async () => {
    setBatchSubmitting(true);
    try {
      await premiumContentApi.setBatch(selectedPremiumFilms, selectedPremiumEpisodes, false, null);
      toast.success('Selected content set as free');
      setSelectedPremiumFilms([]);
      setSelectedPremiumEpisodes([]);
      setSeriesEpisodes({});
      setConfirmBatchFreeDialog(false);
      fetchCompanyData();
    } catch (e) {
      toast.error(e.message || 'Failed to update');
    } finally {
      setBatchSubmitting(false);
    }
  };

  const confirmSetPremium = async () => {
    if (!settingPremium) return;
    
    const price = parseFloat(premiumPrice);
    if (isNaN(price) || price < 0.50 || price > 9.99) {
      toast.error('Price must be between $0.50 and $9.99');
      return;
    }

    try {
      await premiumContentApi.setContent(settingPremium.film_id, true, price);
      toast.success(`Content set as premium at $${price.toFixed(2)}`);
      setSettingPremium(null);
      setPremiumPrice('');
      fetchCompanyData();
    } catch (e) {
      toast.error(e.message || 'Failed to set premium');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadForm.title || (!uploadForm.youtube_id && !uploadForm.vimeo_id)) {
      toast.error('Title and video source are required');
      return;
    }

    if (uploadForm.genres.length === 0) {
      toast.error('Select at least one genre');
      return;
    }

    const submissionData = {
      ...uploadForm,
      thumbnail: uploadForm.youtube_id 
        ? `https://img.youtube.com/vi/${uploadForm.youtube_id}/maxresdefault.jpg`
        : `https://vumbnail.com/${uploadForm.vimeo_id}.jpg`,
      duration: parseInt(uploadForm.duration) || 0,
      episodes: uploadForm.content_type === 'series' ? uploadForm.episodes : undefined,
    };

    // Check if agreement was already accepted for current version
    if (!agreementChecked) {
      try {
        const status = await companyApi.getAgreementStatus();
        setAgreementChecked(true);
        if (status.accepted) {
          setAgreementAccepted(true);
        }
      } catch { /* proceed if endpoint fails */ }
    }

    if (!agreementAccepted) {
      // Need to show agreement popup first
      try {
        const page = await legalApi.getPage('content-creator-agreement');
        setAgreementHtml(page.content || '');
      } catch {
        setAgreementHtml('<p>Unable to load agreement. Please try again.</p>');
      }
      setPendingSubmit(submissionData);
      setShowAgreement(true);
      return;
    }

    // Agreement already accepted — proceed with submission
    await doSubmitContent(submissionData);
  };

  const handleAgreementAccept = async () => {
    try {
      // Pass content info for the immutable agreement record
      const contentInfo = pendingSubmit ? {
        content_id: pendingSubmit.film_id || '',
        content_title: pendingSubmit.title || '',
        content_type: pendingSubmit.content_type || 'movie',
      } : {};
      await companyApi.acceptAgreement(contentInfo);
      setAgreementAccepted(true);
      setShowAgreement(false);
      if (pendingSubmit) {
        await doSubmitContent(pendingSubmit);
        setPendingSubmit(null);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to accept agreement');
    }
  };

  const handleAgreementDecline = () => {
    setShowAgreement(false);
    setPendingSubmit(null);
    toast.error('You must accept the Creator Content Submission Agreement to publish content.');
  };

  const doSubmitContent = async (data) => {
    setUploading(true);
    try {
      await companyApi.submitContent(data);

      toast.success('Content submitted for review!');
      setUploadForm({
        content_type: 'film',
        title: '',
        description: '',
        youtube_id: '',
        vimeo_id: '',
        trailer_youtube_id: '',
        trailer_vimeo_id: '',
        duration: '',
        year: new Date().getFullYear(),
        genres: [],
        categories: [],
        episodes: [],
      });

      const contentData = await companyApi.getContent();
      setContent(contentData);
    } catch (error) {
      toast.error(error.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const addEpisode = () => {
    if (!newEpisode.title) { toast.error('Episode title required'); return; }
    setUploadForm(prev => ({
      ...prev,
      episodes: [...prev.episodes, { ...newEpisode }],
    }));
    setNewEpisode(prev => ({
      ...prev,
      episode_number: prev.episode_number + 1,
      title: '',
      description: '',
      youtube_id: '',
      vimeo_id: '',
      duration: 0,
    }));
    toast.success('Episode added');
  };

  const removeEpisode = (index) => {
    setUploadForm(prev => ({
      ...prev,
      episodes: prev.episodes.filter((_, i) => i !== index),
    }));
  };

  const toggleGenre = (genre) => {
    setUploadForm(prev => ({
      ...prev,
      genres: prev.genres.includes(genre)
        ? prev.genres.filter(g => g !== genre)
        : prev.genres.length < 3 ? [...prev.genres, genre] : prev.genres,
    }));
  };

  const toggleCategory = (cat) => {
    setUploadForm(prev => ({
      ...prev,
      categories: prev.categories.includes(cat)
        ? prev.categories.filter(c => c !== cat)
        : [...prev.categories, cat],
    }));
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-[#00FF9D]/20 text-[#00FF9D] border border-[#00FF9D]/30">
            <CheckCircle className="w-3 h-3" />
            Published
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
            <Clock className="w-3 h-3" />
            Under Review
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30">
            <XCircle className="w-3 h-3" />
            Rejected
          </span>
        );
      case 'paused':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-orange-500/20 text-orange-400 border border-orange-500/30">
            <AlertCircle className="w-3 h-3" />
            Paused
          </span>
        );
      case 'pending_optimization':
        return null; // Handled separately with optimization_status
      default:
        return null;
    }
  };

  const getOptimizationBadge = (film) => {
    if (film.status !== 'pending_optimization') return null;
    if (film.optimization_status === 'failed') {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30" data-testid={`optimization-failed-${film.film_id}`}>
          <XCircle className="w-3 h-3" />
          Optimization Failed
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30" data-testid={`optimizing-badge-${film.film_id}`}>
        <Clock className="w-3 h-3" />
        Optimizing...
      </span>
    );
  };

  const filteredContent = content.filter(film => {
    if (contentFilter !== 'all' && film.status !== contentFilter) {
      // Also show pending_optimization when filtering for "pending"
      if (!(contentFilter === 'pending' && film.status === 'pending_optimization')) return false;
    }
    if (typeFilter === 'film' && film.content_type === 'series') return false;
    if (typeFilter === 'series' && film.content_type !== 'series') return false;
    if (genreFilter !== 'all' && !(film.genres || []).includes(genreFilter)) return false;
    if (searchQuery.trim() && !film.title?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'title': return (a.title || '').localeCompare(b.title || '');
      case 'views': return (b.views || 0) - (a.views || 0);
      case 'rating': return (b.avg_rating || 0) - (a.avg_rating || 0);
      case 'newest':
      default: return (b.created_at || '').localeCompare(a.created_at || '');
    }
  });

  // Real views over time data
  const [viewsOverTime, setViewsOverTime] = useState([]);
  const [viewsPeriod, setViewsPeriod] = useState('30d');

  useEffect(() => {
    const fetchViews = async () => {
      try {
        const data = await creatorChartsApi.getViewsOverTime(viewsPeriod);
        setViewsOverTime((data.chart || []).map(d => ({
          name: new Date(d.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
          views: d.views,
        })));
      } catch { setViewsOverTime([]); }
    };
    fetchViews();
  }, [viewsPeriod]);

  const [ratingDistribution, setRatingDistribution] = useState([]);
  const [ratingPeriod, setRatingPeriod] = useState('total');
  const [ratingTotal, setRatingTotal] = useState(0);

  useEffect(() => {
    const fetchRatings = async () => {
      try {
        const data = await editRequestsApi.getRatingDistribution(ratingPeriod);
        const dist = data.distribution || {};
        setRatingDistribution([
          { name: '5 Stars', value: dist[5] || 0 },
          { name: '4 Stars', value: dist[4] || 0 },
          { name: '3 Stars', value: dist[3] || 0 },
          { name: '2 Stars', value: dist[2] || 0 },
          { name: '1 Star', value: dist[1] || 0 },
        ]);
        setRatingTotal(data.total || 0);
      } catch {}
    };
    fetchRatings();
  }, [ratingPeriod]);

  // Settings edit state
  const [settingsEditing, setSettingsEditing] = useState(false);
  const [settingsForm, setSettingsForm] = useState({});
  const [settingsSubmitting, setSettingsSubmitting] = useState(false);
  const [myEditRequests, setMyEditRequests] = useState([]);

  useEffect(() => {
    if (company) {
      setSettingsForm({
        name: company.name || '',
        contact_email: company.contact_email || '',
        website: company.website || '',
        description: company.description || '',
      });
    }
  }, [company]);

  useEffect(() => {
    const fetchEditRequests = async () => {
      try {
        const data = await editRequestsApi.getMyRequests();
        setMyEditRequests(data.requests || []);
      } catch {}
    };
    fetchEditRequests();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030014] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen bg-[#030014]" data-testid="company-dashboard-page">
        <Navbar />
        <div className="pt-24 pb-12 max-w-4xl mx-auto px-4 md:px-8 text-center">
          <AlertCircle className="w-16 h-16 mx-auto text-gray-600 mb-4" />
          <h1 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            No Studio Found
          </h1>
          <p className="text-gray-400 mb-8">
            You haven't registered a company yet. Register to start uploading content.
          </p>
          <Button onClick={() => navigate('/creator/register')} className="btn-primary">
            Register as Creator
          </Button>
        </div>
      </div>
    );
  }

  if (company.status === 'pending') {
    return (
      <div className="min-h-screen bg-[#030014]" data-testid="company-dashboard-page">
        <Navbar />
        <div className="pt-24 pb-12 max-w-lg mx-auto px-4 md:px-8">
          <div className="admin-card p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-yellow-500/20 flex items-center justify-center">
              <Clock className="w-10 h-10 text-yellow-400" />
            </div>
            <h1
              className="text-2xl font-bold mb-4"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Application Under Review
            </h1>
            <p className="text-gray-300 mb-2">
              Your company <span className="text-[#8B5CF6] font-semibold">"{company.name}"</span> is being reviewed.
            </p>
            <p className="text-gray-500 text-sm mb-8">
              This usually takes 24-48 hours. We'll notify you once approved.
            </p>
            
            <div className="bg-[#030014] rounded-lg p-4 text-left mb-6">
              <h3 className="text-sm font-medium text-gray-400 mb-2">Application Details</h3>
              <p className="text-sm text-gray-300"><strong>Studio:</strong> {company.name}</p>
              <p className="text-sm text-gray-300"><strong>Email:</strong> {company.contact_email}</p>
              <p className="text-sm text-gray-300"><strong>Submitted:</strong> {new Date(company.created_at).toLocaleDateString('en-US')}</p>
            </div>

            <div className="flex flex-col gap-3">
              <Button 
                onClick={handleCheckStatus} 
                disabled={checkingStatus}
                className="btn-primary w-full"
                data-testid="check-status-btn"
              >
                {checkingStatus ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Checking...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Check Status
                  </>
                )}
              </Button>
              <Button onClick={() => navigate('/browse')} className="btn-secondary w-full">
                Browse Content While You Wait
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (company.status === 'rejected') {
    return (
      <div className="min-h-screen bg-[#030014]" data-testid="company-dashboard-page">
        <Navbar />
        <div className="pt-24 pb-12 max-w-lg mx-auto px-4 md:px-8">
          <div className="admin-card p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
              <XCircle className="w-10 h-10 text-red-400" />
            </div>
            <h1
              className="text-2xl font-bold mb-4"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Application Not Approved
            </h1>
            <p className="text-gray-400 mb-4">
              Unfortunately, your application was not approved at this time.
            </p>
            {company.rejection_reason && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6 text-left">
                <h3 className="text-sm font-medium text-red-400 mb-1">Reason</h3>
                <p className="text-sm text-gray-300">{company.rejection_reason}</p>
              </div>
            )}
            <p className="text-gray-500 text-sm">
              If you believe this was a mistake, please contact support.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030014]" data-testid="company-dashboard-page">
      <Navbar />

      <div className="pt-24 pb-12 max-w-[1600px] mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <h1
              className="text-3xl md:text-4xl font-bold tracking-tighter"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              {company.name}
            </h1>
            <span className="badge bg-[#00FF9D]/20 text-[#00FF9D] border-[#00FF9D]/30">
              <CheckCircle className="w-3 h-3 mr-1" />
              Verified Creator
            </span>
          </div>
          <p className="text-gray-400">Creator Dashboard</p>
        </div>

        {/* Stats Cards */}
        {analytics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="admin-card p-5 group hover:border-[#8B5CF6]/40 transition-all">
              <div className="flex items-center justify-between mb-3">
                <Eye className="w-6 h-6 text-[#8B5CF6]" />
                <TrendingUp className="w-4 h-4 text-[#00FF9D]" />
              </div>
              <p className="text-3xl font-bold">{analytics.total_views.toLocaleString()}</p>
              <p className="text-sm text-gray-400">Total Views</p>
            </div>
            <div className="admin-card p-5 group hover:border-[#8B5CF6]/40 transition-all">
              <div className="flex items-center justify-between mb-3">
                <Clock className="w-6 h-6 text-blue-400" />
                <span className="text-xs text-gray-500">hours</span>
              </div>
              <p className="text-3xl font-bold">{analytics.total_watch_hours}</p>
              <p className="text-sm text-gray-400">Watch Hours</p>
            </div>
            <div className="admin-card p-5 group hover:border-[#8B5CF6]/40 transition-all">
              <div className="flex items-center justify-between mb-3">
                <Star className="w-6 h-6 text-yellow-400" />
                <span className="text-xs text-[#00FF9D]">★★★★★</span>
              </div>
              <p className="text-3xl font-bold">{analytics.average_rating}</p>
              <p className="text-sm text-gray-400">Avg Rating</p>
            </div>
            <div className="admin-card p-5 group hover:border-[#8B5CF6]/40 transition-all">
              <div className="flex items-center justify-between mb-3">
                <Film className="w-6 h-6 text-pink-400" />
                <span className="text-xs text-gray-500">{analytics.pending_films} pending</span>
              </div>
              <p className="text-3xl font-bold">{analytics.approved_films}</p>
              <p className="text-sm text-gray-400">Published Films</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="content" className="space-y-6" onValueChange={(val) => {
          if (val === 'notifications' && notifUnreadCount > 0) {
            notificationsApi.markAllRead().then(() => setNotifUnreadCount(0)).catch(() => {});
          }
        }}>
          <TabsList className="bg-[#0F0B1E] border border-[#8B5CF6]/20 p-1 flex-wrap">
            <TabsTrigger value="content" className="data-[state=active]:bg-[#8B5CF6]/20 px-6">
              <Film className="w-4 h-4 mr-2" />
              My Content
            </TabsTrigger>
            <TabsTrigger value="premium" className="data-[state=active]:bg-[#8B5CF6]/20 px-6">
              <Crown className="w-4 h-4 mr-2" />
              Premium Content
            </TabsTrigger>
            <TabsTrigger value="shop" className="data-[state=active]:bg-[#00FF9D]/20 px-6" data-testid="shop-tab">
              <Store className="w-4 h-4 mr-2" />
              Shop
            </TabsTrigger>
            <TabsTrigger value="orders" className="data-[state=active]:bg-[#00FF9D]/20 px-6" data-testid="orders-tab">
              <Package className="w-4 h-4 mr-2" />
              Orders
            </TabsTrigger>
            <TabsTrigger value="financials" className="data-[state=active]:bg-[#00FF9D]/20 px-6" data-testid="financials-tab">
              <Wallet className="w-4 h-4 mr-2" />
              Revenue
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-[#8B5CF6]/20 px-6">
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="audience" className="data-[state=active]:bg-[#8B5CF6]/20 px-6" data-testid="audience-tab">
              <Globe className="w-4 h-4 mr-2" />
              Audience
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-[#8B5CF6]/20 px-6">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </TabsTrigger>
            <TabsTrigger value="notifications" className="data-[state=active]:bg-yellow-500/20 px-6 relative" data-testid="notifications-tab">
              <Bell className="w-4 h-4 mr-2" />
              Notifications
              {notifUnreadCount > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-red-500 text-white leading-none" data-testid="notif-badge">{notifUnreadCount}</span>
              )}
            </TabsTrigger>
            <a
              href="https://www.info-reframes.com/marketing-strategy-for-creators/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-1.5 rounded-md text-sm font-semibold text-white transition-colors ml-2"
              style={{ backgroundColor: '#10B981' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#34D399'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = '#10B981'}
              data-testid="guidelines-link"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Guide Lines
            </a>
          </TabsList>

          {/* My Content Tab */}
          <TabsContent value="content">
            <div className="space-y-6">
              {/* Drafts Section */}
              {drafts.length > 0 && (
                <div className="admin-card p-4 border border-amber-500/20" data-testid="drafts-section">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Edit className="w-4 h-4 text-amber-400" />
                      <h3 className="text-sm font-semibold text-white">Drafts</h3>
                      <span className="text-xs text-gray-500">({drafts.length})</span>
                    </div>
                    <span className="text-[10px] text-gray-600">Drafts expire after 14 days</span>
                  </div>
                  <div className="space-y-2">
                    {drafts.map(draft => (
                      <div key={draft.draft_id} className="flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors group" data-testid={`draft-${draft.draft_id}`}>
                        <div className="w-8 h-8 rounded bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                          {draft.content_type === 'series' ? <Tv className="w-4 h-4 text-amber-400" /> : <Film className="w-4 h-4 text-amber-400" />}
                        </div>
                        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/company/upload?draft=${draft.draft_id}`)}>
                          <p className="text-sm font-medium text-white truncate">
                            {draft.form?.title || 'Untitled'} <span className="text-xs text-gray-500 ml-1">({draft.content_type || 'unknown'})</span>
                          </p>
                          <p className="text-[10px] text-gray-500">
                            Last updated: {new Date(draft.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            {draft.form?.video_file ? ' — Video uploaded' : draft.upload_id ? ' — Video uploading' : ''}
                          </p>
                          {draft.upload_id && !draft.form?.video_file && (
                            <p className="text-[10px] text-amber-400 mt-0.5">Upload incomplete — re-select the same video file to resume</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-purple-400 hover:text-purple-300"
                            onClick={() => navigate(`/company/upload?draft=${draft.draft_id}`)} data-testid={`resume-draft-${draft.draft_id}`}>
                            Resume
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-red-400 hover:text-red-300"
                            onClick={() => setConfirmDeleteDraft(draft.draft_id)} data-testid={`delete-draft-${draft.draft_id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Filter Bar */}
              <div className="admin-card p-4">
                <div className="flex flex-col lg:flex-row gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search by title..." className="pl-10 bg-[#030014] border-[#8B5CF6]/20 h-10" data-testid="content-search" />
                  </div>
                  <Select value={genreFilter} onValueChange={setGenreFilter}>
                    <SelectTrigger className="w-40 bg-[#030014] border-[#8B5CF6]/20 h-10" data-testid="genre-filter">
                      <SelectValue placeholder="Genre" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0F0B1E] border-[#8B5CF6]/20 max-h-64">
                      <SelectItem value="all">All Genres</SelectItem>
                      {GENRE_OPTIONS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={contentFilter} onValueChange={setContentFilter}>
                    <SelectTrigger className="w-40 bg-[#030014] border-[#8B5CF6]/20 h-10" data-testid="status-filter">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0F0B1E] border-[#8B5CF6]/20">
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="approved">Published</SelectItem>
                      <SelectItem value="pending">Pending Review</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                      <SelectItem value="paused">Paused</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-40 bg-[#030014] border-[#8B5CF6]/20 h-10" data-testid="sort-filter">
                      <ArrowUpDown className="w-3 h-3 mr-1" />
                      <SelectValue placeholder="Sort" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0F0B1E] border-[#8B5CF6]/20">
                      <SelectItem value="newest">Newest First</SelectItem>
                      <SelectItem value="title">Title A-Z</SelectItem>
                      <SelectItem value="views">Most Views</SelectItem>
                      <SelectItem value="rating">Highest Rated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-800">
                  <span className="text-sm text-gray-400">
                    {filteredContent.length} {filteredContent.length === 1 ? 'item' : 'items'}
                    {(contentFilter !== 'all' || genreFilter !== 'all' || searchQuery) && ' (filtered)'}
                  </span>
                  <Button onClick={() => navigate('/company/upload')} className="btn-primary" data-testid="upload-new-btn">
                    <Plus className="w-4 h-4 mr-2" /> Upload New
                  </Button>
                </div>
              </div>

              {filteredContent.length > 0 ? (
                <div className="space-y-8">
                  {/* Films Section */}
                  {filteredContent.filter(c => c.content_type !== 'series').length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold mb-3 flex items-center gap-2" data-testid="films-section-header">
                        <Film className="w-5 h-5 text-[#8B5CF6]" /> Movies
                        <span className="text-sm text-gray-500 font-normal">({filteredContent.filter(c => c.content_type !== 'series').length})</span>
                      </h3>
                      <div className="grid gap-3">
                        {filteredContent.filter(c => c.content_type !== 'series').map((film) => (
                          <ContentRow key={film.film_id} film={film} getStatusBadge={getStatusBadge} getOptimizationBadge={getOptimizationBadge} onDetails={(f) => navigate(`/company/content/${f.film_id}/performance`)} onResubmit={(filmId) => {
                            const filmObj = content.find(f => f.film_id === filmId);
                            if (filmObj) openEditRejected(filmObj);
                          }} />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Series Section */}
                  {filteredContent.filter(c => c.content_type === 'series').length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold mb-3 flex items-center gap-2" data-testid="series-section-header">
                        <Tv className="w-5 h-5 text-[#00FF9D]" /> Series
                        <span className="text-sm text-gray-500 font-normal">({filteredContent.filter(c => c.content_type === 'series').length})</span>
                      </h3>
                      <div className="grid gap-3">
                        {filteredContent.filter(c => c.content_type === 'series').map((series) => (
                          <SeriesRow key={series.film_id} series={series} getStatusBadge={getStatusBadge} onDetails={(s) => navigate(`/company/content/${s.film_id}/performance`)} onResubmit={(filmId) => {
                            const filmObj = content.find(f => f.film_id === filmId);
                            if (filmObj) openEditRejected(filmObj);
                          }} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-16 admin-card">
                  <Film className="w-16 h-16 mx-auto text-gray-600 mb-4" />
                  <h3 className="text-xl font-semibold mb-2">
                    {content.length === 0 ? 'No content yet' : 'No results match your filters'}
                  </h3>
                  <p className="text-gray-400">
                    {content.length === 0 ? 'Click "Upload New" above to add your first content' : 'Try adjusting your filters'}
                  </p>
                </div>
              )}
            </div>
          </TabsContent>


          {/* Premium Content Tab */}
          <TabsContent value="premium">
            <div className="space-y-6">
              {/* Eligibility Status */}
              <div className="admin-card p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Crown className="w-6 h-6 text-yellow-400" />
                  <h3 className="text-xl font-semibold">Premium Content (Pay-Per-View)</h3>
                </div>
                
                {premiumEligibility ? (
                  <div className="space-y-4">
                    {premiumEligibility.eligible ? (
                      <div className="flex items-center gap-2 text-[#00FF9D]">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">You are eligible to sell premium content!</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-yellow-400">
                        <AlertCircle className="w-5 h-5" />
                        <span className="font-medium">Requirements not yet met</span>
                      </div>
                    )}
                    
                    {/* Requirements Progress */}
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className={`p-4 rounded-lg border ${premiumEligibility.requirements?.watch_hours?.met ? 'border-[#00FF9D]/30 bg-[#00FF9D]/5' : 'border-gray-700 bg-[#0F0B1E]'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-400">Watch Hours</span>
                          {premiumEligibility.requirements?.watch_hours?.met && <CheckCircle className="w-4 h-4 text-[#00FF9D]" />}
                        </div>
                        <p className="text-2xl font-bold">{premiumEligibility.requirements?.watch_hours?.current || 0} / {premiumEligibility.requirements?.watch_hours?.required || 1000}</p>
                        <p className="text-xs text-gray-500">Total hours watched on your content</p>
                      </div>
                      
                      <div className={`p-4 rounded-lg border ${premiumEligibility.requirements?.content_count?.met ? 'border-[#00FF9D]/30 bg-[#00FF9D]/5' : 'border-gray-700 bg-[#0F0B1E]'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-400">Content Pieces</span>
                          {premiumEligibility.requirements?.content_count?.met && <CheckCircle className="w-4 h-4 text-[#00FF9D]" />}
                        </div>
                        <p className="text-2xl font-bold">{premiumEligibility.requirements?.content_count?.current || 0} / {premiumEligibility.requirements?.content_count?.required || 10}</p>
                        <p className="text-xs text-gray-500">Approved content on platform</p>
                      </div>
                      
                      <div className={`p-4 rounded-lg border ${premiumEligibility.requirements?.days_active?.met ? 'border-[#00FF9D]/30 bg-[#00FF9D]/5' : 'border-gray-700 bg-[#0F0B1E]'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-400">Days Active</span>
                          {premiumEligibility.requirements?.days_active?.met && <CheckCircle className="w-4 h-4 text-[#00FF9D]" />}
                        </div>
                        <p className="text-2xl font-bold">{premiumEligibility.requirements?.days_active?.current || 0} / {premiumEligibility.requirements?.days_active?.required || 30}</p>
                        <p className="text-xs text-gray-500">Days since approval</p>
                      </div>
                    </div>

                    {/* Premium Content Limit */}
                    {premiumEligibility.eligible && (
                      <div className="bg-[#0F0B1E] rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400">Premium Content Limit</span>
                          <span className="font-medium">{premiumEligibility.premium_content?.current || 0} / {premiumEligibility.premium_content?.max_allowed || 0}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Max {premiumEligibility.premium_content?.percentage_limit}% of your content can be premium</p>
                      </div>
                    )}

                    {/* Price Range Info */}
                    <div className="bg-[#0F0B1E] rounded-lg p-4">
                      <p className="text-sm text-gray-400">Price Range: <span className="text-white font-medium">${premiumEligibility.price_range?.min} - ${premiumEligibility.price_range?.max}</span></p>
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-400">Loading eligibility...</div>
                )}
              </div>

              {/* Premium Content Earnings */}
              {premiumEarnings && premiumEarnings.total_earnings > 0 && (
                <div className="admin-card p-6">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-[#00FF9D]" />
                    Premium Content Earnings
                  </h3>
                  <p className="text-3xl font-bold text-[#00FF9D]">${premiumEarnings.total_earnings.toFixed(2)}</p>
                  <p className="text-sm text-gray-400 mt-1">{premiumEarnings.transactions?.length || 0} sales</p>
                </div>
              )}

              {/* Content List for Premium Settings */}
              {premiumEligibility?.eligible && (
                <div className="admin-card p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold">Manage Premium Content</h3>
                    <div className="flex items-center gap-2">
                      {(selectedPremiumFilms.length > 0 || selectedPremiumEpisodes.length > 0) && (
                        <>
                          <span className="text-xs text-gray-400">{selectedPremiumFilms.length + selectedPremiumEpisodes.length} selected</span>
                          <Button size="sm" variant="outline" className="text-red-400 border-red-400/30 hover:bg-red-400/10"
                            data-testid="remove-premium-btn"
                            onClick={handleBatchRemovePremium} disabled={batchSubmitting}>
                            <Unlock className="w-3.5 h-3.5 mr-1" /> Make Free
                          </Button>
                          <Button size="sm" className="bg-yellow-500 hover:bg-yellow-600 text-black font-medium"
                            data-testid="set-premium-btn"
                            onClick={() => { setShowSetPremiumPopup(true); setBatchPremiumPrice(''); }}>
                            <Crown className="w-3.5 h-3.5 mr-1" /> Set Premium
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Search */}
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <input
                      value={premiumSearch}
                      onChange={e => setPremiumSearch(e.target.value)}
                      placeholder="Search content..."
                      className="w-full bg-[#030014] border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-gray-300 placeholder-gray-600"
                      data-testid="premium-search-input"
                    />
                  </div>

                  {/* Movies / Series toggle */}
                  <div className="flex gap-1 mb-4">
                    <button onClick={() => setPremiumContentView('movies')}
                      className={`text-sm px-4 py-1.5 rounded-lg transition-colors ${premiumContentView === 'movies' ? 'bg-[#8B5CF6] text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}
                      data-testid="premium-tab-movies">
                      <Film className="w-3.5 h-3.5 inline mr-1.5" /> Movies
                    </button>
                    <button onClick={() => setPremiumContentView('series')}
                      className={`text-sm px-4 py-1.5 rounded-lg transition-colors ${premiumContentView === 'series' ? 'bg-[#8B5CF6] text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}
                      data-testid="premium-tab-series">
                      <Tv className="w-3.5 h-3.5 inline mr-1.5" /> Series
                    </button>
                  </div>

                  {/* Movies List */}
                  {premiumContentView === 'movies' && (
                    <div className="space-y-2">
                      {content
                        .filter(f => f.status === 'approved' && f.content_type === 'film')
                        .filter(f => !premiumSearch || f.title?.toLowerCase().includes(premiumSearch.toLowerCase()))
                        .map(film => (
                          <div key={film.film_id} className={`flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${selectedPremiumFilms.includes(film.film_id) ? 'bg-[#8B5CF6]/10 border-[#8B5CF6]/40' : 'bg-[#0F0B1E] border-white/5 hover:border-white/10'}`}
                            data-testid={`premium-movie-${film.film_id}`}
                            onClick={() => toggleSelectFilm(film.film_id)}>
                            <input type="checkbox" checked={selectedPremiumFilms.includes(film.film_id)} readOnly
                              className="accent-[#8B5CF6] w-4 h-4 flex-shrink-0" />
                            {film.thumbnail && <img src={film.thumbnail} alt="" className="w-14 h-9 object-cover rounded flex-shrink-0" />}
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{film.title}</p>
                              <p className="text-xs text-gray-500">{film.views || 0} views</p>
                            </div>
                            {film.is_premium_content && (
                              <span className="text-yellow-400 text-xs font-medium flex items-center gap-1 flex-shrink-0">
                                <Lock className="w-3 h-3" /> ${film.premium_price}
                              </span>
                            )}
                          </div>
                        ))}
                      {content.filter(f => f.status === 'approved' && f.content_type === 'film').filter(f => !premiumSearch || f.title?.toLowerCase().includes(premiumSearch.toLowerCase())).length === 0 && (
                        <p className="text-gray-500 text-sm text-center py-8">No movies found</p>
                      )}
                    </div>
                  )}

                  {/* Series List */}
                  {premiumContentView === 'series' && (
                    <div className="space-y-2">
                      {content
                        .filter(f => f.status === 'approved' && f.content_type === 'series')
                        .filter(f => !premiumSearch || f.title?.toLowerCase().includes(premiumSearch.toLowerCase()))
                        .map(series => (
                          <div key={series.film_id} data-testid={`premium-series-${series.film_id}`}>
                            <div className={`flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${expandedSeries === series.film_id ? 'bg-[#0F0B1E] border-[#8B5CF6]/30' : 'bg-[#0F0B1E] border-white/5 hover:border-white/10'}`}
                              onClick={() => toggleExpandSeries(series.film_id)}>
                              {expandedSeries === series.film_id ? <ChevronDown className="w-4 h-4 text-[#8B5CF6] flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />}
                              {series.thumbnail && <img src={series.thumbnail} alt="" className="w-14 h-9 object-cover rounded flex-shrink-0" />}
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">{series.title}</p>
                                <p className="text-xs text-gray-500">{series.views || 0} views</p>
                              </div>
                              <span className="text-xs text-gray-500 flex-shrink-0">
                                {seriesEpisodes[series.film_id]?.length || '...'} episodes
                              </span>
                            </div>

                            {/* Expanded episodes */}
                            {expandedSeries === series.film_id && (
                              <div className="ml-6 mt-1 space-y-1 border-l-2 border-[#8B5CF6]/20 pl-3">
                                {!seriesEpisodes[series.film_id] ? (
                                  <div className="flex items-center gap-2 py-3 text-gray-500 text-sm">
                                    <Loader2 className="w-4 h-4 animate-spin" /> Loading episodes...
                                  </div>
                                ) : seriesEpisodes[series.film_id].length === 0 ? (
                                  <p className="text-gray-500 text-sm py-3">No episodes</p>
                                ) : (
                                  <>
                                    <button className="text-xs text-[#8B5CF6] hover:underline mb-1"
                                      data-testid={`select-all-eps-${series.film_id}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const epIds = seriesEpisodes[series.film_id].map(ep => ep.episode_id);
                                        const allSelected = epIds.every(id => selectedPremiumEpisodes.includes(id));
                                        if (allSelected) {
                                          setSelectedPremiumEpisodes(prev => prev.filter(id => !epIds.includes(id)));
                                        } else {
                                          setSelectedPremiumEpisodes(prev => [...new Set([...prev, ...epIds])]);
                                        }
                                      }}>
                                      {seriesEpisodes[series.film_id].every(ep => selectedPremiumEpisodes.includes(ep.episode_id)) ? 'Deselect all' : 'Select all'}
                                    </button>
                                    {seriesEpisodes[series.film_id].map(ep => (
                                      <div key={ep.episode_id}
                                        className={`flex items-center gap-3 p-2 rounded-md border transition-colors cursor-pointer ${selectedPremiumEpisodes.includes(ep.episode_id) ? 'bg-[#8B5CF6]/10 border-[#8B5CF6]/30' : 'bg-[#030014] border-white/5 hover:border-white/10'}`}
                                        data-testid={`premium-ep-${ep.episode_id}`}
                                        onClick={(e) => { e.stopPropagation(); toggleSelectEpisode(ep.episode_id); }}>
                                        <input type="checkbox" checked={selectedPremiumEpisodes.includes(ep.episode_id)} readOnly
                                          className="accent-[#8B5CF6] w-3.5 h-3.5 flex-shrink-0" />
                                        <span className="text-xs text-[#00FF9D] font-medium flex-shrink-0">S{ep.season_number}E{ep.episode_number}</span>
                                        <span className="text-sm flex-1 min-w-0 truncate">{ep.title}</span>
                                        {ep.is_premium_content && (
                                          <span className="text-yellow-400 text-[10px] font-medium flex items-center gap-0.5 flex-shrink-0">
                                            <Lock className="w-2.5 h-2.5" /> ${ep.premium_price}
                                          </span>
                                        )}
                                      </div>
                                    ))}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      {content.filter(f => f.status === 'approved' && f.content_type === 'series').filter(f => !premiumSearch || f.title?.toLowerCase().includes(premiumSearch.toLowerCase())).length === 0 && (
                        <p className="text-gray-500 text-sm text-center py-8">No series found</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Set Premium Price Dialog - Batch */}
              {showSetPremiumPopup && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" data-testid="set-premium-popup">
                  <div className="admin-card p-6 w-full max-w-md">
                    <h3 className="text-xl font-semibold mb-2">Set Premium Price</h3>
                    <p className="text-gray-400 text-sm mb-4">
                      {selectedPremiumFilms.length > 0 && `${selectedPremiumFilms.length} movie${selectedPremiumFilms.length > 1 ? 's' : ''}`}
                      {selectedPremiumFilms.length > 0 && selectedPremiumEpisodes.length > 0 && ' and '}
                      {selectedPremiumEpisodes.length > 0 && `${selectedPremiumEpisodes.length} episode${selectedPremiumEpisodes.length > 1 ? 's' : ''}`}
                      {' '}will be set as premium pay-per-view content.
                    </p>
                    
                    <div className="mb-4">
                      <label className="block text-sm text-gray-400 mb-2">Price (USD)</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                        <Input
                          type="number"
                          min="0.50"
                          max="9.99"
                          step="0.01"
                          value={batchPremiumPrice}
                          onChange={(e) => setBatchPremiumPrice(e.target.value)}
                          className="pl-8"
                          placeholder="0.50 - 9.99"
                          data-testid="premium-price-input"
                        />
                      </div>
                    </div>
                    
                    <div className="flex gap-3">
                      <Button
                        onClick={() => { setShowSetPremiumPopup(false); setBatchPremiumPrice(''); }}
                        variant="outline"
                        className="flex-1"
                        data-testid="premium-cancel-btn"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleBatchSetPremium}
                        disabled={batchSubmitting}
                        className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black font-medium"
                        data-testid="premium-confirm-btn"
                      >
                        {batchSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Crown className="w-4 h-4 mr-1" />}
                        Confirm
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Legacy single-item Set Premium Price Dialog */}
              {settingPremium && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
                  <div className="admin-card p-6 w-full max-w-md">
                    <h3 className="text-xl font-semibold mb-4">Set Premium Price</h3>
                    <p className="text-gray-400 mb-4">"{settingPremium.title}"</p>
                    
                    <div className="mb-4">
                      <label className="block text-sm text-gray-400 mb-2">Price (USD)</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                        <Input
                          type="number"
                          min="0.50"
                          max="9.99"
                          step="0.01"
                          value={premiumPrice}
                          onChange={(e) => setPremiumPrice(e.target.value)}
                          className="pl-8"
                          placeholder="0.50 - 9.99"
                        />
                      </div>
                    </div>
                    
                    <div className="flex gap-3">
                      <Button
                        onClick={() => { setSettingPremium(null); setPremiumPrice(''); }}
                        variant="outline"
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={confirmSetPremium}
                        className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black"
                      >
                        Confirm
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>


          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <div className="space-y-6">
              {/* Charts Row */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Views Over Time */}
                <div className="admin-card p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-[#8B5CF6]" />
                      Views Over Time
                    </h3>
                    <div className="flex gap-1">
                      {[{v:'7d',l:'7D'},{v:'30d',l:'30D'},{v:'90d',l:'90D'}].map(f => (
                        <button key={f.v} onClick={() => setViewsPeriod(f.v)}
                          className={`text-xs px-2.5 py-1 rounded-md transition-colors ${
                            viewsPeriod === f.v ? 'bg-[#8B5CF6] text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
                          }`} data-testid={`views-filter-${f.v}`}>{f.l}</button>
                      ))}
                    </div>
                  </div>
                  <div className="h-64">
                    {viewsOverTime.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={viewsOverTime}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1A1033" />
                        <XAxis dataKey="name" stroke="#64748b" fontSize={11} interval="preserveStartEnd" />
                        <YAxis stroke="#64748b" fontSize={12} allowDecimals={false} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#0F0B1E',
                            border: '1px solid rgba(139, 92, 246, 0.3)',
                            borderRadius: '8px',
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="views"
                          stroke="#8B5CF6"
                          strokeWidth={3}
                          dot={false}
                          activeDot={{ r: 6, fill: '#00FF9D' }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-gray-500 text-sm">
                        No views data for this period
                      </div>
                    )}
                  </div>
                </div>

                {/* Rating Distribution */}
                <div className="admin-card p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Star className="w-5 h-5 text-yellow-400" />
                      Rating Distribution
                      <span className="text-xs text-gray-500 font-normal">({ratingTotal} ratings)</span>
                    </h3>
                    <div className="flex gap-1">
                      {[{v:'day',l:'Day'},{v:'week',l:'Week'},{v:'month',l:'Month'},{v:'total',l:'Total'}].map(f => (
                        <button key={f.v} onClick={() => setRatingPeriod(f.v)}
                          className={`text-xs px-2.5 py-1 rounded-md transition-colors ${
                            ratingPeriod === f.v ? 'bg-[#8B5CF6] text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
                          }`} data-testid={`rating-filter-${f.v}`}>{f.l}</button>
                      ))}
                    </div>
                  </div>
                  <div className="h-64">
                    {ratingTotal > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={ratingDistribution.filter(d => d.value > 0)}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={90}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {ratingDistribution.filter(d => d.value > 0).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#0F0B1E',
                            border: '1px solid rgba(139, 92, 246, 0.3)',
                            borderRadius: '8px',
                          }}
                        />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-gray-500 text-sm">
                        No ratings data for this period
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Individual Content Performance */}
              <div className="admin-card p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Film className="w-5 h-5 text-pink-400" />
                  Content Performance
                </h3>
                
                {analytics?.film_stats && analytics.film_stats.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Content</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Status</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-gray-400">Views</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-gray-400">Rating</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-gray-400">Watch Hours</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-gray-400">Completion Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.film_stats.map((film) => (
                          <tr key={film.film_id} className="border-b border-white/5 hover:bg-white/5">
                            <td className="py-4 px-4">
                              <p className="font-medium">{film.title}</p>
                            </td>
                            <td className="py-4 px-4">{getStatusBadge(film.status)}</td>
                            <td className="py-4 px-4 text-right">{film.views.toLocaleString()}</td>
                            <td className="py-4 px-4 text-right">
                              <span className="text-yellow-400">{film.avg_rating} ★</span>
                              <span className="text-gray-500 text-sm ml-1">({film.rating_count})</span>
                            </td>
                            <td className="py-4 px-4 text-right">{film.watch_hours}h</td>
                            <td className="py-4 px-4 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <div className="w-20 h-2 bg-white/10 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-[#00FF9D]"
                                    style={{ width: `${Math.min(100, (film.watch_hours / (film.views * 0.1)) * 100)}%` }}
                                  />
                                </div>
                                <span className="text-sm">{Math.round(Math.min(100, (film.watch_hours / (film.views * 0.1)) * 100))}%</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <BarChart3 className="w-12 h-12 mx-auto text-gray-600 mb-4" />
                    <p className="text-gray-400">No analytics data yet. Upload some content to get started!</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Audience Tab */}
          <TabsContent value="audience" data-testid="audience-content">
            <CreatorAudienceTab />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="max-w-2xl">
              <div className="admin-card p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold flex items-center gap-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    <Settings className="w-5 h-5 text-[#8B5CF6]" />
                    Studio Settings
                  </h2>
                  {!settingsEditing ? (
                    <button onClick={() => setSettingsEditing(true)}
                      className="text-sm px-4 py-1.5 rounded-lg bg-[#8B5CF6]/10 text-[#8B5CF6] hover:bg-[#8B5CF6]/20 transition-colors"
                      data-testid="settings-edit-btn">
                      Edit
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button onClick={() => { setSettingsEditing(false); setSettingsForm({ name: company.name || '', contact_email: company.contact_email || '', website: company.website || '', description: company.description || '' }); }}
                        className="text-sm px-3 py-1.5 rounded-lg bg-white/5 text-gray-400 hover:bg-white/10 transition-colors">
                        Cancel
                      </button>
                      <button onClick={async () => {
                        setSettingsSubmitting(true);
                        try {
                          await editRequestsApi.submitSettingsEdit(settingsForm);
                          toast.success('Changes submitted for review');
                          setSettingsEditing(false);
                          const data = await editRequestsApi.getMyRequests();
                          setMyEditRequests(data.requests || []);
                        } catch (e) { toast.error(e.message || 'Failed to submit'); }
                        finally { setSettingsSubmitting(false); }
                      }}
                        disabled={settingsSubmitting}
                        className="text-sm px-4 py-1.5 rounded-lg bg-[#8B5CF6] text-white hover:bg-[#7C3AED] disabled:opacity-50 transition-colors"
                        data-testid="settings-submit-btn">
                        {settingsSubmitting ? 'Submitting...' : 'Submit for Review'}
                      </button>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-gray-400 mb-1.5 block">Studio Name</label>
                    <Input
                      value={settingsEditing ? settingsForm.name : company.name}
                      onChange={e => setSettingsForm(p => ({...p, name: e.target.value}))}
                      disabled={!settingsEditing}
                      className="bg-[#030014] border-[#8B5CF6]/20 h-11"
                      data-testid="settings-name"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-1.5 block">Contact Email</label>
                    <Input
                      value={settingsEditing ? settingsForm.contact_email : company.contact_email}
                      onChange={e => setSettingsForm(p => ({...p, contact_email: e.target.value}))}
                      disabled={!settingsEditing}
                      className="bg-[#030014] border-[#8B5CF6]/20 h-11"
                      data-testid="settings-email"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-1.5 block">Website</label>
                    <Input
                      value={settingsEditing ? settingsForm.website : (company.website || '')}
                      onChange={e => setSettingsForm(p => ({...p, website: e.target.value}))}
                      disabled={!settingsEditing}
                      className="bg-[#030014] border-[#8B5CF6]/20 h-11"
                      data-testid="settings-website"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-1.5 block">Description</label>
                    <Textarea
                      value={settingsEditing ? settingsForm.description : company.description}
                      onChange={e => setSettingsForm(p => ({...p, description: e.target.value}))}
                      disabled={!settingsEditing}
                      className="bg-[#030014] border-[#8B5CF6]/20 min-h-[100px]"
                      data-testid="settings-description"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-1.5 block">Account Status</label>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(company.status)}
                      <span className="text-sm text-gray-500">
                        Since {new Date(company.approved_at || company.created_at).toLocaleDateString('en-US')}
                      </span>
                    </div>
                  </div>
                </div>

                {!settingsEditing && (
                  <div className="pt-4 border-t border-white/10">
                    <p className="text-sm text-gray-500">
                      Click "Edit" to modify settings. All changes require owner/admin approval.
                    </p>
                  </div>
                )}

                {/* Pending Edit Requests */}
                {myEditRequests.filter(r => r.type === 'settings_edit').length > 0 && (
                  <div className="pt-4 border-t border-white/10">
                    <h4 className="text-sm font-semibold mb-2 text-gray-400">Pending Changes</h4>
                    {myEditRequests.filter(r => r.type === 'settings_edit').slice(0, 3).map(r => (
                      <div key={r.request_id} className="text-xs py-1.5">
                        <div className="flex items-center gap-2">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            r.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                            r.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>{r.status}</span>
                          <span className="text-gray-500">{new Date(r.created_at).toLocaleDateString('en-US')}</span>
                        </div>
                        {r.review_reason && (
                          <p className="text-gray-500 mt-1 ml-1 italic">Reason: {r.review_reason}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Shop Tab */}
          <TabsContent value="shop" data-testid="shop-content">
            <CreatorProductsPanel onRefresh={fetchCompanyData} />
          </TabsContent>

          {/* Creator Orders Tab */}
          <TabsContent value="orders" data-testid="orders-content">
            <CreatorOrdersPanelComponent />
          </TabsContent>

          {/* Unified Financials Tab */}
          <TabsContent value="financials" data-testid="financials-content">
            <FinancialsDashboard />
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" data-testid="notifications-content">
            <CreatorNotificationsPanel onReadAll={() => setNotifUnreadCount(0)} />
          </TabsContent>
        </Tabs>

        {/* Agreement Popup */}
        <Dialog open={showAgreement} onOpenChange={(open) => { if (!open) handleAgreementDecline(); }}>
          <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col bg-[#0F0B1E] border-white/10 p-0" data-testid="agreement-dialog">
            <DialogHeader className="px-6 pt-6 pb-3 border-b border-white/10 shrink-0">
              <DialogTitle className="text-lg font-bold">Creator Content Submission Agreement</DialogTitle>
              <p className="text-sm text-gray-400 mt-1">You must accept this agreement before publishing content.</p>
            </DialogHeader>
            <div
              className="flex-1 overflow-y-auto px-6 py-4 text-sm text-gray-300 leading-relaxed agreement-content"
              data-testid="agreement-scroll-area"
              dangerouslySetInnerHTML={{ __html: agreementHtml.replace(/color:\s*red/gi, 'color:#00FF9D').replace(/color:\s*#[fF]{2}0{4}/gi, 'color:#00FF9D').replace(/class="[^"]*text-red[^"]*"/gi, (m) => m.replace(/text-red-\d+/g, 'text-[#00FF9D]')) }}
              style={{ maxHeight: '50vh' }}
            />
            <div className="px-6 py-4 border-t border-white/10 flex gap-3 justify-end shrink-0">
              <Button variant="outline" onClick={handleAgreementDecline} className="border-white/10" data-testid="agreement-decline-btn">
                Decline
              </Button>
              <Button onClick={handleAgreementAccept} className="btn-primary" data-testid="agreement-accept-btn">
                I Accept
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Confirm Make Free Dialog */}
        <Dialog open={confirmFreeDialog.open} onOpenChange={(o) => !o && setConfirmFreeDialog({ open: false, film: null })}>
          <DialogContent className="bg-[#0F0B1E] border-white/10 max-w-md" data-testid="confirm-free-dialog">
            <DialogHeader>
              <DialogTitle>Make Content Free?</DialogTitle>
            </DialogHeader>
            <p className="text-gray-400 text-sm mt-2">
              Are you sure you want to make <span className="text-white font-medium">"{confirmFreeDialog.film?.title}"</span> free? This content will no longer require payment to watch.
            </p>
            <div className="flex gap-3 mt-6 justify-end">
              <Button variant="outline" onClick={() => setConfirmFreeDialog({ open: false, film: null })} className="border-white/10" data-testid="cancel-free-btn">Cancel</Button>
              <Button onClick={confirmMakeFree} className="bg-red-500 hover:bg-red-600" data-testid="confirm-free-btn">Yes, Make Free</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Confirm Batch Make Free Dialog */}
        <Dialog open={confirmBatchFreeDialog} onOpenChange={(o) => !o && setConfirmBatchFreeDialog(false)}>
          <DialogContent className="bg-[#0F0B1E] border-white/10 max-w-md" data-testid="confirm-batch-free-dialog">
            <DialogHeader>
              <DialogTitle>Make Selected Content Free?</DialogTitle>
            </DialogHeader>
            <p className="text-gray-400 text-sm mt-2">
              Are you sure you want to make <span className="text-white font-medium">{selectedPremiumFilms.length + selectedPremiumEpisodes.length} item(s)</span> free? They will no longer require payment to watch.
            </p>
            <div className="flex gap-3 mt-6 justify-end">
              <Button variant="outline" onClick={() => setConfirmBatchFreeDialog(false)} className="border-white/10">Cancel</Button>
              <Button onClick={confirmBatchMakeFree} disabled={batchSubmitting} className="bg-red-500 hover:bg-red-600" data-testid="confirm-batch-free-btn">
                {batchSubmitting ? 'Processing...' : 'Yes, Make Free'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Rejected Content Dialog */}
        <Dialog open={!!editingContent} onOpenChange={(o) => !o && setEditingContent(null)}>
          <DialogContent className="bg-[#0F0B1E] border-white/10 max-w-3xl max-h-[85vh] overflow-y-auto p-0" data-testid="edit-rejected-dialog">
            <DialogHeader className="px-6 pt-5 pb-3 border-b border-white/10 sticky top-0 bg-[#0F0B1E] z-10">
              <DialogTitle>Edit & Resubmit Content</DialogTitle>
            </DialogHeader>
            {editingContent && (
              <div className="px-6 py-4 space-y-5">
                {editingContent.rejection_feedback && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded text-sm text-red-400">
                    <strong>Rejection reason:</strong> {editingContent.rejection_feedback}
                  </div>
                )}
                {editingContent.paused_reason && editingContent.status === 'paused' && (
                  <div className="p-3 bg-orange-500/10 border border-orange-500/20 rounded text-sm text-orange-400">
                    <strong>Paused reason:</strong> {editingContent.paused_reason}
                  </div>
                )}

                {/* Series/Movie title & description */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-[#00FF9D]">
                    {editingContent.content_type === 'series' ? 'Series Details' : 'Movie Details'}
                  </h3>
                  <div>
                    <label className="text-sm text-gray-400 mb-1.5 block">Title</label>
                    <Input value={editForm.title} onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))} className="bg-[#030014] border-[#8B5CF6]/20" data-testid="edit-title" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-1.5 block">Description</label>
                    <Textarea value={editForm.description} onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))} className="bg-[#030014] border-[#8B5CF6]/20 min-h-[80px]" data-testid="edit-description" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-1.5 block">Thumbnail</label>
                    {editForm.thumbnail && <img src={editForm.thumbnail.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${editForm.thumbnail}` : editForm.thumbnail} alt="thumb" className="w-40 h-24 object-cover rounded mb-2" />}
                    <input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && handleEditThumbnailUpload(e.target.files[0])} className="text-sm text-gray-400" data-testid="edit-thumbnail-input" />
                  </div>
                  {editingContent.content_type !== 'series' && (
                    <div>
                      <label className="text-sm text-gray-400 mb-1.5 block">Video</label>
                      {editForm.video_file && <p className="text-xs text-gray-500 mb-1">Current: {editForm.video_file.split('/').pop()}</p>}
                      <input type="file" accept="video/*" onChange={(e) => e.target.files?.[0] && handleEditVideoUpload(e.target.files[0])} className="text-sm text-gray-400" data-testid="edit-video-input" />
                    </div>
                  )}
                </div>

                {/* Episodes section for series */}
                {editingContent.content_type === 'series' && editEpisodes.length > 0 && (
                  <div className="space-y-3 pt-2 border-t border-white/10">
                    <h3 className="text-sm font-semibold text-[#8B5CF6]">Episodes ({editEpisodes.length})</h3>
                    {editEpisodes.map((ep, idx) => {
                      const orig = editEpisodesOriginal.find(o => o.episode_id === ep.episode_id);
                      const isModified = orig && (ep.title !== orig.title || ep.description !== orig.description || ep.thumbnail !== orig.thumbnail || ep.video_file !== orig.video_file);
                      return (
                        <details key={ep.episode_id} className={`rounded-lg border ${isModified ? 'border-[#8B5CF6]/40 bg-[#8B5CF6]/5' : 'border-white/5 bg-[#030014]'}`} data-testid={`edit-episode-${ep.episode_id}`}>
                          <summary className="p-3 cursor-pointer flex items-center gap-2 text-sm">
                            <span className="text-[#00FF9D] font-bold text-xs w-12 flex-shrink-0">S{ep.season_number}E{ep.episode_number}</span>
                            <span className="flex-1 truncate">{ep.title}</span>
                            {isModified && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#8B5CF6]/20 text-[#8B5CF6] flex-shrink-0">MODIFIED</span>}
                            {ep.status === 'rejected' && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 flex-shrink-0">REJECTED</span>}
                          </summary>
                          <div className="px-3 pb-3 space-y-3 border-t border-white/5 pt-3">
                            {ep.rejection_feedback && (
                              <div className="p-2 bg-red-500/10 border border-red-500/20 rounded text-xs text-red-400">
                                <strong>Feedback:</strong> {ep.rejection_feedback}
                              </div>
                            )}
                            <div>
                              <label className="text-xs text-gray-400 mb-1 block">Title</label>
                              <Input value={ep.title} onChange={(e) => setEditEpisodes(prev => prev.map((x, i) => i === idx ? { ...x, title: e.target.value } : x))} className="bg-[#0F0B1E] border-[#8B5CF6]/20 h-9 text-sm" />
                            </div>
                            <div>
                              <label className="text-xs text-gray-400 mb-1 block">Description</label>
                              <Textarea value={ep.description || ''} onChange={(e) => setEditEpisodes(prev => prev.map((x, i) => i === idx ? { ...x, description: e.target.value } : x))} className="bg-[#0F0B1E] border-[#8B5CF6]/20 min-h-[60px] text-sm" />
                            </div>
                            <div>
                              <label className="text-xs text-gray-400 mb-1 block">Thumbnail</label>
                              {ep.thumbnail && <img src={ep.thumbnail.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${ep.thumbnail}` : ep.thumbnail} alt="" className="w-28 h-16 object-cover rounded mb-1.5" />}
                              <input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && handleEpThumbnailUpload(idx, e.target.files[0])} className="text-xs text-gray-400" />
                            </div>
                            <div>
                              <label className="text-xs text-gray-400 mb-1 block">Video</label>
                              {ep.video_file && <p className="text-[10px] text-gray-500 mb-1 truncate">Current: {ep.video_file.split('/').pop()}</p>}
                              <input type="file" accept="video/*" onChange={(e) => e.target.files?.[0] && handleEpVideoUpload(idx, e.target.files[0])} className="text-xs text-gray-400" />
                            </div>
                          </div>
                        </details>
                      );
                    })}
                  </div>
                )}

                <div className="flex gap-3 justify-end pt-2 border-t border-white/10 sticky bottom-0 bg-[#0F0B1E] py-4">
                  <Button variant="outline" onClick={() => setEditingContent(null)} className="border-white/10">Cancel</Button>
                  <Button onClick={handleEditResubmit} disabled={editUploading || !editForm.title.trim()} className="btn-primary" data-testid="edit-resubmit-btn">
                    {editUploading ? 'Uploading...' : 'Save & Resubmit'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Draft Confirmation Dialog */}
        <Dialog open={!!confirmDeleteDraft} onOpenChange={() => setConfirmDeleteDraft(null)}>
          <DialogContent className="bg-[#0F0B1E] border-red-500/20 max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-white">Delete Draft</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-gray-400">
              Delete this draft? The uploaded video and thumbnail will be permanently removed.
            </p>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" onClick={() => setConfirmDeleteDraft(null)} data-testid="cancel-delete-draft">Cancel</Button>
              <Button className="bg-red-600 hover:bg-red-700 text-white" disabled={!!deletingDraftId}
                onClick={() => handleDeleteDraft(confirmDeleteDraft)} data-testid="confirm-delete-draft">
                {deletingDraftId ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
                Delete
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

const CreatorNotificationsPanel = ({ onReadAll }) => {
  const [notifications, setNotifications] = React.useState([]);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [loading, setLoading] = React.useState(true);

  const fetchNotifications = React.useCallback(async () => {
    try {
      const data = await notificationsApi.getAll(100);
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (e) {
      console.error('Failed to fetch notifications:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      if (onReadAll) onReadAll();
    } catch (e) { console.error(e); }
  };

  const getNotifIcon = (type) => {
    switch (type) {
      case 'edit_approved': return <CheckCircle className="w-5 h-5 text-[#00FF9D]" />;
      case 'edit_rejected': return <XCircle className="w-5 h-5 text-red-400" />;
      case 'content_paused': return <AlertCircle className="w-5 h-5 text-yellow-400" />;
      case 'content_approved': return <CheckCircle className="w-5 h-5 text-[#00FF9D]" />;
      case 'content_rejected': return <XCircle className="w-5 h-5 text-red-400" />;
      case 'content_deleted': return <AlertCircle className="w-5 h-5 text-red-400" />;
      case 'content_published': return <Play className="w-5 h-5 text-[#00FF9D]" />;
      default: return <Bell className="w-5 h-5 text-[#8B5CF6]" />;
    }
  };

  return (
    <div className="space-y-4" data-testid="creator-notifications-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold">Notifications</h3>
          {unreadCount > 0 && (
            <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-red-500 text-white">{unreadCount}</span>
          )}
        </div>
        {unreadCount > 0 && (
          <button onClick={handleMarkAllRead} className="text-xs text-[#8B5CF6] hover:underline" data-testid="mark-all-read-btn">
            Mark all as read
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><div className="spinner" /></div>
      ) : notifications.length > 0 ? (
        <div className="space-y-2">
          {notifications.map(notif => (
            <div
              key={notif.notification_id}
              className={`admin-card p-4 flex items-start gap-3 transition-colors ${!notif.read ? 'border-l-2 border-l-[#8B5CF6] bg-[#8B5CF6]/5' : ''}`}
              data-testid={`notification-${notif.notification_id}`}
            >
              <div className="mt-0.5 shrink-0">{getNotifIcon(notif.type)}</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{notif.title}</p>
                <p className="text-xs text-gray-400 mt-0.5">{notif.message}</p>
                <p className="text-[10px] text-gray-600 mt-1">{new Date(notif.created_at).toLocaleString()}</p>
              </div>
              {!notif.read && <div className="w-2 h-2 rounded-full bg-[#8B5CF6] shrink-0 mt-2" />}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 admin-card">
          <Bell className="w-10 h-10 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">No notifications yet</p>
          <p className="text-xs text-gray-600 mt-1">You'll receive notifications about approvals, rejections, and content updates here.</p>
        </div>
      )}
    </div>
  );
};

export default CompanyDashboardPage;
